import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field, change } from 'redux-form';
import { Form, FormGroup, Col, Label } from 'reactstrap';
import { toastr } from 'react-redux-toastr';
import Immutable from 'immutable';
import Alert from '../../../common/informative/Alert';
import FormItem from '../../../common/forms/FormItem';
import {
  isRequired,
  validateLength,
  validateNumber,
  validateRuc,
} from '../../../../utils/validators';
import {
  getDriverBusAssignments,
  getInternalParcelByCorrelation,
  postBusiness,
  postCustomer,
} from '../../../../actions';
import Select from '../../../common/forms/select/Select';
import TextInput from '../../../common/forms/input/TextInput';
import CargoLocationSelect from '../../../common/forms/select/CargoLocationSelect';
import RegisteredBusSelect from '../../../common/forms/select/RegisteredBusSelect';
import DynamicForm from '../../../common/forms/DynamicForm';
import { generateManualGrrColumns } from '../../../../config/dynamicFormFields';
import FormFooter from '../../../common/forms/FormFooter';
import DatePicker from '../../../common/forms/input/DatePicker';
import Loader from '../../../common/Loader';
import {
  GRR_MOTIVE_INTERNAL_TRANSFER,
  MANUAL_GRR_MOTIVE_OPTIONS,
  MANUAL_GRR_VOUCHER_TYPES_OPTIONS,
  MAX_LENGTH_TEXTAREA_FOR_INTERNAL_PARCEL,
} from '../../../../config/constants';
import Modal from '../../../common/modal/Modal';
import InternalParcelSearchForm from './internal-parcel-search/InternalParcelSearchForm';
import DistrictSelect from '../../../common/forms/select/DistrictSelect';

const validateLength11 = validateLength(11, 'dígitos');

const validateFields = (values) => {
  const errors = {};

  if (!values.cargoItems || !values.cargoItems.length) {
    errors.itemsError = 'Debe ingresar un item';
  }

  if (!values.externalDriverList || !values.externalDriverList.length) {
    errors.driversError = 'Debe ingresar un coductor';
  }

  return errors;
};

export const MassiveSenderRemissionGuideForm = ({
  handleSubmit,
  dispatchGetDriverBusAssignments,
  onSubmit,
  drivers,
  loading,
  dispatchGetInternalParcelByCorrelation,
  dispatchChange,
}) => {
  const [motiveId] = useState(GRR_MOTIVE_INTERNAL_TRANSFER);
  const [sourceLocation, setSourceLocation] = useState(0);
  const [refDocumentRequired, setRefDocumentRequired] = useState(false);
  const [showInternalParcelModal, setShowInternalParcelModal] = useState(false);
  const [internalParcels, setInternalParcels] = useState([]);
  const parcelIds = internalParcels.map((p) => p.id);

  const onHandleSubmit = (formValues) => {
    onSubmit(
      formValues,
      drivers.get('content'),
      refDocumentRequired,
      parcelIds,
    );
  };

  const handleLocationChange = (location, name) => {
    if (name === 'sourceLocation') {
      setSourceLocation({ sourceCityId: location.city ? location.city.id : 0 });
    }
  };

  const handleRefDocumentRequired = (event) => {
    setRefDocumentRequired(event.target.checked);
  };

  const decorateItemsWithCorrelative = (parcels) =>
    parcels.flatMap((p) =>
      (p.internalCargoItemList || []).map((item) => {
        const fullDocumentCode =
          p.fullDocumentCode || `${p.documentSeries}-${p.documentCode}`;
        // Ajusta "description" por el campo que muestres en la grilla
        const baseDesc = item.description || item.name || '';
        return {
          ...item,
          description: baseDesc
            ? `${baseDesc} - ${fullDocumentCode}`
            : fullDocumentCode,
        };
      }),
    );

  const onInternalParcelSearch = async (formValues) => {
    const newFormValues = {
      documentSeries: formValues.internalParcelDocumentSerie,
      documentCode: formValues.internalParcelDocumentCorrelation,
    };

    const internalParcel = await dispatchGetInternalParcelByCorrelation(
      newFormValues,
    );
    if (!internalParcel) return;

    if (internalParcels.length > 0) {
      const currentSourceId = internalParcels[0].sourceLocationId;
      const newSourceId = internalParcel.sourceLocationId;
      if (currentSourceId !== newSourceId) {
        toastr.error(
          'Error',
          'Todos los traslados deben tener el mismo origen',
        );
        return;
      }
    }

    if (internalParcels.some((p) => p.id === internalParcel.id)) {
      return;
    }

    const updated = [...internalParcels, internalParcel];
    setInternalParcels(updated);

    if (updated.length === 1) {
      const firstSource = {
        value: internalParcel.sourceLocationId,
        label: internalParcel.sourceLocationName,
      };
      setSourceLocation(internalParcel.sourceLocationId);
      dispatchChange(
        'MassiveSenderRemissionGuideForm',
        'sourceLocation',
        firstSource,
      );
    }

    const allItems = decorateItemsWithCorrelative(updated);
    dispatchChange('MassiveSenderRemissionGuideForm', 'cargoItems', allItems);

    const allNotes = updated
      .map((p) => p.note)
      .filter(Boolean)
      .join(' | ');
    dispatchChange('MassiveSenderRemissionGuideForm', 'comments', allNotes);

    setShowInternalParcelModal(false);
  };

  const handleRemoveInternalParcel = (id) => {
    const updated = internalParcels.filter((p) => p.id !== id);
    setInternalParcels(updated);

    const allItems = decorateItemsWithCorrelative(updated);
    dispatchChange('MassiveSenderRemissionGuideForm', 'cargoItems', allItems);

    const allNotes = updated
      .map((p) => p.note)
      .filter(Boolean)
      .join(' | ');
    dispatchChange('MassiveSenderRemissionGuideForm', 'comments', allNotes);
  };

  const renderConsigneeField = () => (
    <Alert message="El destinatario es Turismo Civa SAC" />
  );

  const onRegisteredBusChange = (bus) => {
    if (bus.value) {
      dispatchGetDriverBusAssignments({ query: `companyBusId:${bus.label}` });
    }
  };

  const renderDynamicForm = () => {
    if (sourceLocation === 0) {
      return <Alert message="Seleccione Origen y Destino" />;
    }
    return (
      <>
        <DynamicForm
          name="cargoItems"
          columns={generateManualGrrColumns(
            motiveId === GRR_MOTIVE_INTERNAL_TRANSFER,
          )}
          showAddButton={motiveId !== GRR_MOTIVE_INTERNAL_TRANSFER}
          showRemoveButton={motiveId !== GRR_MOTIVE_INTERNAL_TRANSFER}
        />
      </>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onHandleSubmit)}>
        <FormGroup row>
          <FormItem label="Motivo de Traslado" required>
            <Field
              name="motiveId"
              component={Select}
              isDisabled
              type="text"
              placeholder="Seleccione un Motivo"
              validate={[isRequired]}
              options={MANUAL_GRR_MOTIVE_OPTIONS}
            />
          </FormItem>
        </FormGroup>

        {motiveId === GRR_MOTIVE_INTERNAL_TRANSFER && (
          <>
            <FormGroup row>
              <Col>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowInternalParcelModal(true)}
                >
                  Agregar Traslado Interno
                </button>
              </Col>
            </FormGroup>

            {internalParcels.length > 0 && (
              <FormGroup row>
                <Col>
                  <Label>Traslados Internos Seleccionados:</Label>
                  <ul className="list-group">
                    {internalParcels.map((p) => (
                      <li
                        key={p.id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {p.fullDocumentCode}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveInternalParcel(p.id)}
                        >
                          Eliminar
                        </button>
                      </li>
                    ))}
                  </ul>
                </Col>
              </FormGroup>
            )}
          </>
        )}

        <FormGroup row>
          <FormItem label="Observaciones del Traslado" required>
            <Field
              name="comments"
              type="textarea"
              component={TextInput}
              placeholder="Observaciones del Traslado"
              validate={[isRequired]}
              maxLength={MAX_LENGTH_TEXTAREA_FOR_INTERNAL_PARCEL}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Instrucciones del Traslado" required>
            <Field
              name="motiveDescription"
              type="textarea"
              component={TextInput}
              placeholder="Instrucciones del Traslado"
              validate={[isRequired]}
              maxLength={MAX_LENGTH_TEXTAREA_FOR_INTERNAL_PARCEL}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Origen" required>
            <Field
              name="sourceLocation"
              component={CargoLocationSelect}
              isDisabled
              onChange={(option) =>
                handleLocationChange(option, 'sourceLocation')
              }
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        {motiveId === GRR_MOTIVE_INTERNAL_TRANSFER && (
          <FormGroup row>
            <FormItem label="Destino" required>
              <Field
                name="destinationLocation"
                component={CargoLocationSelect}
                onChange={(option) =>
                  handleLocationChange(option, 'destinationLocation')
                }
              />
            </FormItem>
          </FormGroup>
        )}
        {motiveId !== GRR_MOTIVE_INTERNAL_TRANSFER && (
          <>
            <FormGroup row>
              <FormItem label="Dirección de Destino" required>
                <Field
                  name="destinationAddress"
                  component={TextInput}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>
            <FormGroup row>
              <FormItem label="Distrito Destino" required>
                <Field
                  name="districtId"
                  component={DistrictSelect}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>
          </>
        )}

        <FormGroup row>
          <FormItem label="Fecha de Salida" required>
            <Field
              name="itineraryDepartureDate"
              component={DatePicker}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <h3>Documento Adjunto</h3>
        <Label>
          <Field
            name="refDocumentRequired"
            component="input"
            type="checkbox"
            onChange={handleRefDocumentRequired}
          />{' '}
          Se va adjuntar un documento?
        </Label>
        {refDocumentRequired && (
          <FormGroup row className="d-flex align-items-center flex-wrap">
            <Col xs="12" sm="auto">
              <Label for="voucherType" className="mb-0">
                Tipo de Documento:
              </Label>
            </Col>
            <Col
              xs="12"
              sm="12"
              md="12"
              lg="3"
              xl="2"
              className="input-container"
            >
              <Field
                name="voucherType"
                type="text"
                component={Select}
                options={MANUAL_GRR_VOUCHER_TYPES_OPTIONS}
                validate={refDocumentRequired ? [isRequired] : []}
              />
            </Col>
            <Col xs="12" sm="auto">
              <Label for="documentSeries" className="mb-0">
                Código del Documento:
              </Label>
            </Col>
            <Col
              xs="12"
              sm="12"
              md="12"
              lg="2"
              xl="1"
              className="input-container"
            >
              <Field
                name="documentSeries"
                type="text"
                component={TextInput}
                validate={refDocumentRequired ? [isRequired] : []}
              />
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              -
            </Col>
            <Col
              xs="12"
              sm="12"
              md="12"
              lg="2"
              xl="1"
              className="input-container"
            >
              <Field
                name="documentCode"
                type="text"
                component={TextInput}
                validate={refDocumentRequired ? [isRequired] : []}
              />
            </Col>
            <Col xs="12" sm="auto">
              <Label for="issuerDocumentNumber" className="mb-0">
                Número Documento del Emisor:
              </Label>
            </Col>
            <Col
              xs="12"
              sm="12"
              md="12"
              lg="3"
              xl="2"
              className="input-container"
            >
              <Field
                name="issuerDocumentNumber"
                type="text"
                component={TextInput}
                validate={
                  refDocumentRequired
                    ? [
                        isRequired,
                        validateLength11,
                        validateNumber,
                        validateRuc,
                      ]
                    : []
                }
              />
            </Col>
          </FormGroup>
        )}

        <h3>Destinatario</h3>
        {renderConsigneeField()}
        <h3>Datos del Vehículo</h3>
        {motiveId === GRR_MOTIVE_INTERNAL_TRANSFER && (
          <FormGroup row>
            <FormItem label="Número de Bus" required>
              <Field
                name="registeredBusId"
                component={RegisteredBusSelect}
                onChange={onRegisteredBusChange}
                validate={[isRequired]}
                onlyActive
              />
            </FormItem>
          </FormGroup>
        )}
        <h3>Datos de la Encomienda</h3>
        <Field name="itemsError" component={TextInput} type="hidden" />
        {renderDynamicForm()}
        <FormFooter
          saveButtonDisabled={loading}
          saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
        />
      </Form>

      <Modal
        show={showInternalParcelModal}
        title="Buscar Traslado Interno"
        onClickClose={() => setShowInternalParcelModal(false)}
        body={
          <InternalParcelSearchForm
            onCancel={() => setShowInternalParcelModal(false)}
            onSubmit={onInternalParcelSearch}
          />
        }
        size="lg"
      />
    </>
  );
};

MassiveSenderRemissionGuideForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dispatchGetDriverBusAssignments: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  drivers: PropTypes.instanceOf(Immutable.Map).isRequired,
  loading: PropTypes.bool.isRequired,
  dispatchGetInternalParcelByCorrelation: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  drivers: state.TrafficUnit.DriverBusAssignment.getIn(['all', 'content']),
  loading: !state.CargoUnit.ManualSenderRemissionGuide.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  internalParcel: state.CargoUnit.InternalTransfer.getIn([
    'current',
    'content',
  ]),
});

const mapDispatchToProps = {
  dispatchPostCustomer: postCustomer,
  dispatchPostBusiness: postBusiness,
  dispatchGetDriverBusAssignments: getDriverBusAssignments,
  dispatchChange: change,
  dispatchGetInternalParcelByCorrelation: getInternalParcelByCorrelation,
};

const formComponent = reduxForm({
  form: 'MassiveSenderRemissionGuideForm',
  validate: validateFields,
})(MassiveSenderRemissionGuideForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
